// src/pages/Kalender.jsx
// Woche oder Monat oben, Tagesliste darunter.
//
// Die Wochenleiste ist fuer den Monteur gedacht: sieben grosse Flaechen, mit
// dem Daumen zu treffen - er plant seinen Tag, nicht seinen Monat. Das Buero
// wollte zusaetzlich den Monatsblick (Anna-Lena 19.09.2026: "dann hat man
// schon mal so ein bisschen Monatsblick"). Deshalb ein Umschalter:
// Buero startet im Monat, der Monteur in der Woche. Die Wahl merkt sich das
// Geraet, die Tagesliste darunter bleibt in beiden Ansichten dieselbe.

import { useState, useMemo } from 'react';
import { C } from './formui';
import {
  useWartungenImZeitraum, terminText, stationText, ortText, anlageText,
  hinweise, STATUS_TEXT,
} from '../hooks/useWartungen';

const WOCHENTAGE_KURZ = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const ANSICHT_SPEICHER = 'etb-kalender-ansicht';

const iso = (d) => {
  // Nicht toISOString(): das rechnet nach UTC um und verschiebt bei uns
  // abends den Tag.
  const p = (n) => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
};

// Montag der Woche, in der dieses Datum liegt.
function montagVon(datum) {
  const d = new Date(datum);
  d.setHours(12, 0, 0, 0);
  const versatz = (d.getDay() + 6) % 7;   // Sonntag = 0 -> 6
  d.setDate(d.getDate() - versatz);
  return d;
}

// Erster Tag des Monatsgitters: der Montag vor (oder am) Monatsersten.
function gitterStart(datum) {
  const d = new Date(datum);
  d.setHours(12, 0, 0, 0);
  d.setDate(1);
  return montagVon(d);
}

const STATUS_FARBE = {
  vorbereitet: C.gray500,
  zugewiesen: C.navy,
  entwurf: C.orange,
  monteur_abgeschlossen: C.yellow,
  buero_freigegeben: C.green,
  abgeschlossen: C.green,
};

export default function Kalender({ userId, istBuero, onOeffnenProtokoll, onZurueck, onPlanungAendern }) {
  // Gemerkte Ansicht, sonst: Buero Monat, Monteur Woche.
  const [ansicht, setAnsichtRoh] = useState(() => {
    try {
      const g = localStorage.getItem(ANSICHT_SPEICHER);
      if (g === 'woche' || g === 'monat') return g;
    } catch { /* privates Fenster - dann die Vorgabe */ }
    return istBuero ? 'monat' : 'woche';
  });
  const setAnsicht = (a) => {
    setAnsichtRoh(a);
    try { localStorage.setItem(ANSICHT_SPEICHER, a); } catch { /* nur Bequemlichkeit */ }
  };
  const istMonat = ansicht === 'monat';

  // Anker = irgendein Tag im gezeigten Zeitraum (Woche bzw. Monat).
  const [anker, setAnker] = useState(() => new Date());
  const [gewaehlt, setGewaehlt] = useState(() => iso(new Date()));

  // Die Tage, die oben stehen: 7 in der Woche, 42 im Monatsgitter
  // (immer 6 Zeilen - sonst springt die Liste darunter beim Blättern).
  const tage = useMemo(() => {
    const start = istMonat ? gitterStart(anker) : montagVon(anker);
    return Array.from({ length: istMonat ? 42 : 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [anker, istMonat]);

  const von = iso(tage[0]);
  const bis = iso(tage[tage.length - 1]);
  const { wartungen, loading, error } = useWartungenImZeitraum(von, bis, { userId, istBuero });

  const proTag = useMemo(() => {
    const m = {};
    wartungen.forEach(w => {
      const t = w.wartungs_datum;
      if (!t) return;
      (m[t] = m[t] || []).push(w);
    });
    return m;
  }, [wartungen]);

  const heute = iso(new Date());
  const desTages = proTag[gewaehlt] || [];
  const monatVonAnker = anker.getMonth();

  const verschieben = (richtung) => {
    const neu = new Date(anker);
    neu.setHours(12, 0, 0, 0);
    if (istMonat) {
      // Auf den Monatsersten setzen, sonst rutscht der 31. in den übernächsten Monat.
      neu.setDate(1);
      neu.setMonth(neu.getMonth() + richtung);
    } else {
      neu.setDate(neu.getDate() + richtung * 7);
    }
    setAnker(neu);
    // Die Auswahl mitnehmen, damit nicht ein Tag außerhalb des Zeitraums gewählt bleibt.
    const gewaehltDatum = new Date(gewaehlt + 'T12:00:00');
    if (istMonat) {
      const tag = Math.min(gewaehltDatum.getDate(), new Date(neu.getFullYear(), neu.getMonth() + 1, 0).getDate());
      setGewaehlt(iso(new Date(neu.getFullYear(), neu.getMonth(), tag)));
    } else {
      const neuGewaehlt = montagVon(neu);
      neuGewaehlt.setDate(neuGewaehlt.getDate() + ((gewaehltDatum.getDay() + 6) % 7));
      setGewaehlt(iso(neuGewaehlt));
    }
  };

  const zuHeute = () => {
    setAnker(new Date());
    setGewaehlt(heute);
  };

  const titel = istMonat
    ? MONATE[monatVonAnker] + ' ' + anker.getFullYear()
    : (tage[0].getMonth() === tage[6].getMonth()
      ? MONATE[tage[0].getMonth()] + ' ' + tage[0].getFullYear()
      : MONATE[tage[0].getMonth()] + ' – ' + MONATE[tage[6].getMonth()] + ' ' + tage[6].getFullYear());

  // Punkte unter der Tageszahl: bis zu drei, danach ein "+".
  const punkte = (anzahl, hell) => (
    <span style={{ height: 6, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
      {anzahl > 0 && Array.from({ length: Math.min(anzahl, 3) }).map((_, k) => (
        <span key={k} style={{ width: 5, height: 5, borderRadius: '50%', background: hell ? C.white : C.green }}/>
      ))}
      {anzahl > 3 && <span style={{ fontSize: 10, fontWeight: 800, color: hell ? C.white : C.green }}>+</span>}
    </span>
  );

  const umschalter = (
    <div style={{ display: 'flex', gap: 4, background: C.gray100, borderRadius: 8, padding: 3 }}>
      {[['woche', 'Woche'], ['monat', 'Monat']].map(([wert, text]) => (
        <button key={wert} type="button" onClick={() => setAnsicht(wert)}
          style={{ minHeight: 34, padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                   fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
                   background: ansicht === wert ? C.white : 'transparent',
                   color: ansicht === wert ? C.navy : C.gray700,
                   boxShadow: ansicht === wert ? '0 1px 3px rgba(0,0,0,0.12)' : 'none' }}>
          {text}
        </button>
      ))}
    </div>
  );

  return (
    <div style={{ background: C.bg, minHeight: '100vh', paddingBottom: 90 }}>
      {/* Kopf mit Woche bzw. Monat */}
      <div style={{ background: C.white, borderBottom: '1px solid ' + C.gray200,
                    position: 'sticky', top: 0, zIndex: 40 }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <button type="button" onClick={() => verschieben(-1)} aria-label={istMonat ? 'Monat zurück' : 'Woche zurück'}
              style={pfeilStil}>‹</button>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.navy }}>{titel}</div>
              <button type="button" onClick={zuHeute}
                style={{ background: 'none', border: 'none', color: C.green, fontSize: 13,
                         fontWeight: 700, cursor: 'pointer', padding: '2px 6px', fontFamily: 'inherit' }}>
                zu heute
              </button>
            </div>
            <button type="button" onClick={() => verschieben(1)} aria-label={istMonat ? 'Monat vor' : 'Woche vor'}
              style={pfeilStil}>›</button>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>{umschalter}</div>

          {/* Im Monat eine Kopfzeile mit den Wochentagen - im Gitter steht sie
              sonst nicht mehr an jeder Zelle. */}
          {istMonat && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, marginTop: 8 }}>
              {WOCHENTAGE_KURZ.map(w => (
                <div key={w} style={{ textAlign: 'center', fontSize: 12, fontWeight: 700, color: C.gray500 }}>{w}</div>
              ))}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: istMonat ? 3 : 4,
                        marginTop: istMonat ? 3 : 8 }}>
            {tage.map((d, i) => {
              const t = iso(d);
              const anzahl = (proTag[t] || []).length;
              const istHeute = t === heute;
              const istGewaehlt = t === gewaehlt;
              const fremderMonat = istMonat && d.getMonth() !== monatVonAnker;
              return (
                <button key={t} type="button" onClick={() => setGewaehlt(t)}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                    padding: istMonat ? '5px 1px 4px' : '8px 2px 6px', borderRadius: istMonat ? 8 : 10,
                    minHeight: istMonat ? 44 : 62,
                    border: istGewaehlt ? '2px solid ' + C.navy : '2px solid transparent',
                    background: istGewaehlt ? C.navy : (istHeute ? C.greenBg : 'transparent'),
                    color: istGewaehlt ? C.white : (fremderMonat ? C.gray300 : C.navy),
                    opacity: fremderMonat && !istGewaehlt ? 0.65 : 1,
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}>
                  {!istMonat && (
                    <span style={{ fontSize: 12, fontWeight: 600,
                                   color: istGewaehlt ? 'rgba(255,255,255,0.75)' : C.gray500 }}>
                      {WOCHENTAGE_KURZ[i]}
                    </span>
                  )}
                  <span style={{ fontSize: istMonat ? 15 : 17, fontWeight: 800 }}>{d.getDate()}</span>
                  {punkte(anzahl, istGewaehlt)}
                </button>
              );
            })}
          </div>

          {/* Im Monat unter dem Gitter, wie viele Wartungen der Monat insgesamt hat. */}
          {istMonat && (
            <div style={{ textAlign: 'center', fontSize: 13, color: C.gray500, marginTop: 8 }}>
              {(() => {
                const imMonat = wartungen.filter(w => {
                  const d = new Date((w.wartungs_datum || '') + 'T12:00:00');
                  return !isNaN(d) && d.getMonth() === monatVonAnker;
                }).length;
                if (loading && imMonat === 0) return 'lädt …';
                return imMonat === 0 ? 'In diesem Monat ist nichts geplant.'
                  : imMonat === 1 ? '1 Termin in diesem Monat' : imMonat + ' Termine in diesem Monat';
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Tagesliste */}
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '16px 14px' }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.gray500, textTransform: 'uppercase',
                      letterSpacing: 0.6, marginBottom: 10 }}>
          {new Date(gewaehlt + 'T12:00:00').toLocaleDateString('de-DE',
            { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>

        {error && (
          <div style={{ padding: 14, background: C.redBg, border: '2px solid ' + C.red,
                        borderRadius: 8, color: C.red, fontWeight: 700, fontSize: 15 }}>
            {error}
          </div>
        )}

        {loading && desTages.length === 0 && (
          <div style={{ color: C.gray500, fontSize: 15, padding: '20px 0', textAlign: 'center' }}>
            lädt …
          </div>
        )}

        {!loading && desTages.length === 0 && !error && (
          <div style={{ background: C.white, borderRadius: 12, padding: 24, textAlign: 'center',
                        color: C.gray500, fontSize: 15 }}>
            An diesem Tag ist keine Wartung eingetragen.
          </div>
        )}

        {desTages.map(w => {
          const hin = hinweise(w);
          return (
            <div key={w.id} style={{ marginBottom: 10 }}>
            <button type="button" onClick={() => onOeffnenProtokoll && onOeffnenProtokoll(w.id)}
              style={{ display: 'block', width: '100%', textAlign: 'left', background: C.white,
                       border: '1px solid ' + C.gray200, borderLeft: '5px solid ' + (STATUS_FARBE[w.status] || C.gray300),
                       borderRadius: 12, padding: 14, cursor: 'pointer',
                       fontFamily: 'inherit' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 17, fontWeight: 800, color: C.navy }}>
                  {w.kunden?.name || w.kunde_name || stationText(w)}
                </span>
                <span style={{ fontSize: 15, fontWeight: 700, color: C.gray700 }}>
                  {terminText(w.wartungs_datum, w.wartungs_uhrzeit)}
                </span>
              </div>
              <div style={{ fontSize: 15, color: C.gray700, marginTop: 2 }}>{ortText(w)}</div>
              <div style={{ fontSize: 14, color: C.gray500, marginTop: 2 }}>{anlageText(w)}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, padding: '3px 9px', borderRadius: 6,
                               background: C.gray100, color: C.gray700 }}>
                  {STATUS_TEXT[w.status] || w.status}
                </span>
                {hin.map(h => (
                  <span key={h} style={{ fontSize: 13, fontWeight: 700, padding: '3px 9px',
                                         borderRadius: 6, background: C.yellowBg, color: C.navy }}>
                    {h}
                  </span>
                ))}
                {w.zugewiesen_an && (
                  <span style={{ fontSize: 13, padding: '3px 9px', borderRadius: 6,
                                 background: C.gray100, color: C.gray700 }}>
                    mit {w.zugewiesen_an}
                  </span>
                )}
              </div>
              {w.plan_bemerkung && (
                <div style={{ fontSize: 14, color: C.gray700, marginTop: 8, fontStyle: 'italic' }}>
                  {w.plan_bemerkung}
                </div>
              )}
            </button>
            {/* Das Buero kann einen Termin hier direkt verschieben oder umplanen -
                solange die Wartung nicht abgeschlossen ist. */}
            {istBuero && onPlanungAendern && ['vorbereitet', 'zugewiesen', 'entwurf'].includes(w.status) && (
              <div style={{ marginTop: 6, textAlign: 'right' }}>
                <button type="button" onClick={() => onPlanungAendern(w.id)}
                  style={{ minHeight: 40, padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
                           fontSize: 14, fontWeight: 700, fontFamily: 'inherit', background: C.white,
                           color: C.navy, border: '1.5px solid ' + C.gray300 }}>
                  Planung ändern
                </button>
              </div>
            )}
            </div>
          );
        })}

        {onZurueck && (
          <button type="button" onClick={onZurueck}
            style={{ marginTop: 12, padding: '10px 18px', borderRadius: 8, border: '1.5px solid ' + C.gray200,
                     background: C.white, color: C.navy, fontWeight: 700, fontSize: 15,
                     cursor: 'pointer', fontFamily: 'inherit' }}>
            ‹ Zurück
          </button>
        )}
      </div>
    </div>
  );
}

const pfeilStil = {
  minWidth: 44, minHeight: 44, borderRadius: 10, border: '1px solid ' + C.gray200,
  background: C.white, color: C.navy, fontSize: 22, fontWeight: 700,
  cursor: 'pointer', lineHeight: 1, fontFamily: 'inherit',
};
